import { useTerminology } from '../../../terminology/TerminologyContext';
import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import api, { getCsrfToken, getCases } from "../../services/api";
import ThemeContext from "../../ThemeContext";
import ClientConfig from "../../../config/Clientconfig";
import { URL_SEGMENTS } from "../../../config/Routesconfig";

// ─────────────────────────────────────────────────────────────────────────────
// Read user ONCE at module level — never re-read localStorage on every render
// ─────────────────────────────────────────────────────────────────────────────
const _storedUser   = JSON.parse(localStorage.getItem("user")) || {};
const _isClientUser = (_storedUser?.role || "").toLowerCase() === "client_user";

// ─────────────────────────────────────────────────────────────────────────────
// SessionsCell — renders "completed of total" or "0" or "…"
// ─────────────────────────────────────────────────────────────────────────────
const SessionsCell = React.memo(({ stats, loading }) => {
  useTerminology();
  if (loading) return <span style={{ color: "#aaa" }}>…</span>;
  const { total, completed } = stats;
  if (total === 0) return <span>0</span>;
  return <span>{completed} of {total}</span>;
});

// ─────────────────────────────────────────────────────────────────────────────
// CaseManagement
// ─────────────────────────────────────────────────────────────────────────────
const CaseManagement = () => {
  useTerminology();
  const { theme }    = useContext(ThemeContext);
  const { username } = useParams();
  const navigate     = useNavigate();

  const fontFamily =
    theme.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const [cases,              setCases]              = useState([]);
  const [searchTerm,         setSearchTerm]         = useState("");
  const [highlightedCaseId,  setHighlightedCaseId]  = useState(null);
  const [hoveredCaseId,      setHoveredCaseId]      = useState(null);
  const searchInputRef                              = useRef(null);
  const [currentPage,        setCurrentPage]        = useState(1);
  const [perPage,            setPerPage]            = useState(10);
  const [gotoPage,           setGotoPage]           = useState("");
  const [isLoading,          setIsLoading]          = useState(false);

  // { [caseId]: { total: number, completed: number } }
  const [sessionStats,  setSessionStats]  = useState({});
  // { [caseId]: boolean } — true while still fetching
  const [statsLoading,  setStatsLoading]  = useState({});

  // ── Base routes ───────────────────────────────────────────────────────────
  const dashBase = `/${username}/dashboard`;
  const routes = {
    casesList  : `${dashBase}/${URL_SEGMENTS.cases}`,
    addCase    : `${dashBase}/${URL_SEGMENTS.addCase}`,
    editCase   : (id) => `${dashBase}/${URL_SEGMENTS.editCase}/${id}`,
    viewCase   : (id) => `${dashBase}/${URL_SEGMENTS.viewCase}/${id}`,
    sampleCase : `${dashBase}/${URL_SEGMENTS.sampleCase}`,
  };

  // ── FETCH ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      try {
        setIsLoading(true);
        await getCsrfToken();

        // Fire cases + session-counts in parallel
        const [casesRes, countsRes] = await Promise.all([
          getCases(),
          api.get("/cases/session-counts").catch(() => ({ data: { data: {} } })),
        ]);

        if (cancelled) return;

        const realCases = casesRes.data.data || [];
        const counts    = countsRes.data?.data || {};

        // FIX: call ensure in parallel with the above, so we get the real
        // sample case (with correct created_at = admin's account date) from the DB.
        // Then check if getCases() already returned a sample case — if yes, don't
        // add a duplicate frontend-only object.
        let allCases = [...realCases];

        // Check if real cases already contain the sample case
        const alreadyHasSample = realCases.some((c) => c.is_sample);

        if (!alreadyHasSample) {
          // Ensure sample case exists in DB and get it back (with admin's created_at)
          try {
            const sampleRes = await api.post("/cases/sample/ensure");
            const realSample = sampleRes.data?.data;

            if (realSample) {
              // Tag it so the UI knows to route to the sample view
              realSample.isSample = true;
              allCases = [...realCases, realSample];

              // Also pull its session count into the map using its real DB id
              if (counts[realSample.id]) {
                // already in counts map — nothing to do
              }
            }
          } catch {
            // If ensure fails, still show a placeholder so the row appears
            // but with no date (shown as "—") and no sessions
            const fallback = {
              id        : "sample_Case",
              case_name : "Sample Case (DEMO)",
              sessions  : null,
              created_at: null,
              isSample  : true,
            };
            allCases = [...realCases, fallback];
          }
        } else {
          // Real cases already include the sample — just tag it
          allCases = realCases.map((c) =>
            c.is_sample ? { ...c, isSample: true } : c
          );
        }

        setCases(allCases);
        setSessionStats(counts);

        const loadingMap = {};
        allCases.forEach((c) => (loadingMap[c.id] = false));
        setStatsLoading(loadingMap);

      } catch {
        if (!cancelled) {
          toast.error(`Failed to fetch ${ClientConfig.cases.pluralLabel}`);
          setCases([]);
          setSessionStats({});
          setStatsLoading({});
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchAll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── SEARCH ────────────────────────────────────────────────────────────────
  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
    setHighlightedCaseId(null);
    setCurrentPage(1);
  }, []);

  const handleSearchSubmit = useCallback(() => {
    const search = searchTerm.toLowerCase();
    const matchedIndex = filteredCases.findIndex(
      (c) =>
        c.id.toString() === searchTerm ||
        c.case_name.toLowerCase().includes(search) ||
        (c.created_at && new Date(c.created_at).toLocaleDateString().includes(searchTerm))
    );
    if (matchedIndex !== -1) {
      const matchedCase = filteredCases[matchedIndex];
      setCurrentPage(Math.ceil((matchedIndex + 1) / perPage));
      setHighlightedCaseId(matchedCase.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, perPage]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === "Enter") handleSearchSubmit();
  }, [handleSearchSubmit]);

  // ── DELETE ────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async (id) => {
    if (id === "sample_Case" || cases.find((c) => c.id === id)?.isSample) {
      toast.warning(`Sample ${ClientConfig.cases.label} cannot be deleted`);
      return;
    }
    if (!window.confirm(`Are you sure you want to delete this ${ClientConfig.cases.label}?`)) return;
    try {
      setIsLoading(true);
      await api.delete(`/cases/${id}`);
      setCases((prev) => prev.filter((c) => c.id !== id));
      setSessionStats((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });
      toast.success(`${ClientConfig.cases.label} deleted successfully`);
    } catch {
      toast.error(`Failed to delete ${ClientConfig.cases.label}`);
    } finally {
      setIsLoading(false);
    }
  }, [cases]);

  // ── FILTER ────────────────────────────────────────────────────────────────
  const filteredCases = cases.filter((c) => {
    const s = searchTerm.toLowerCase();
    return (
      c.id.toString().includes(s) ||
      c.case_name.toLowerCase().includes(s) ||
      (c.created_at && new Date(c.created_at).toLocaleDateString().includes(s))
    );
  });

  // ── PAGINATION ────────────────────────────────────────────────────────────
  const totalPages     = Math.ceil(filteredCases.length / perPage);
  const paginatedCases = filteredCases.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <>
      {isLoading && (
        <div
          className="position-fixed top-50 start-50 translate-middle"
          style={{ zIndex: 9999 }}
        >
          <div
            className="spinner-border"
            style={{
              width      : "40px",
              height     : "40px",
              borderWidth: "4px",
              color      : theme.colors.primary,
            }}
          />
        </div>
      )}

      <div
        style={{
          backgroundColor: "#ffffff",
          padding        : "1rem",
          borderRadius   : "10px",
          boxShadow      : "0 4px 20px rgba(0,0,0,0.15)",
          fontFamily,
          marginTop      : "10px",
        }}
      >
        {/* ── HEADER ──────────────────────────────────────────────────────── */}
        <div
          style={{
            display       : "flex",
            flexDirection : isMobile ? "column" : "row",
            alignItems    : isMobile ? "stretch" : "center",
            justifyContent: "space-between",
            marginBottom  : "1rem",
            gap           : "0.5rem",
          }}
        >
          <h4
            style={{
              fontSize    : "14px",
              fontWeight  : "bold",
              marginBottom: 0,
              color       : theme.colors.textPrimary,
            }}
          >
            {ClientConfig.cases.managementLabel}
          </h4>

          <div
            style={{
              display       : "flex",
              alignItems    : "center",
              gap           : "0.5rem",
              flexWrap      : "wrap",
              justifyContent: isMobile ? "stretch" : "flex-end",
            }}
          >
            {/* SEARCH */}
            <div style={{ position: "relative", maxWidth: "140px", height: "38px" }}>
              <input
                type="text"
                placeholder={`Search ${ClientConfig.cases.label}`}
                value={searchTerm}
                onChange={handleSearch}
                onKeyPress={handleKeyPress}
                ref={searchInputRef}
                style={{
                  width          : "100%",
                  height         : "100%",
                  border         : "1px solid #dee2e6",
                  borderRadius   : "25px",
                  outline        : "none",
                  padding        : "0 40px 0 14px",
                  fontSize       : "12px",
                  backgroundColor: "#ffffff",
                  fontFamily,
                }}
              />
              <button
                onClick={handleSearchSubmit}
                style={{
                  position      : "absolute",
                  right         : "2px",
                  top           : "2px",
                  width         : "34px",
                  height        : "34px",
                  background    : theme.gradients.primary,
                  border        : "none",
                  borderRadius  : "50%",
                  display       : "flex",
                  alignItems    : "center",
                  justifyContent: "center",
                  cursor        : "pointer",
                }}
              >
                <i className="bi bi-search" style={{ color: "#fff", fontSize: "14px" }} />
              </button>
            </div>

            {/* ADD CASE — hidden for client_user */}
            {!_isClientUser && (
              <Link
                to={routes.addCase}
                style={{
                  background    : theme.gradients.secondary,
                  color         : theme.colors.textWhite,
                  padding       : "6px 12px",
                  borderRadius  : "8px",
                  fontSize      : "clamp(10px, 2.5vw, 11px)",
                  textDecoration: "none",
                  minWidth      : "120px",
                  textAlign     : "center",
                }}
              >
                Add {ClientConfig.cases.label}
              </Link>
            )}
          </div>
        </div>

        {/* ── TABLE ───────────────────────────────────────────────────────── */}
        <div
          style={{
            overflowX      : "auto",
            backgroundColor: "#ffffff",
            borderRadius   : "10px",
            border         : "1px solid #dee2e6",
          }}
        >
          <table
            style={{
              width         : "100%",
              borderCollapse: "collapse",
              fontSize      : "clamp(12px, 2.5vw, 14px)",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: theme.colors.primary, color: theme.colors.textWhite }}>
                <th className="p-2">{ClientConfig.cases.label} No</th>
                <th className="p-2">{ClientConfig.cases.label} Name</th>
                <th className="p-2">{ClientConfig.sessions.pluralLabel}</th>
                <th className="p-2">Created</th>
                <th className="p-2" style={{ minWidth: "150px" }}></th>
              </tr>
            </thead>

            <tbody>
              {paginatedCases.length > 0 ? (
                paginatedCases.map((c) => {
                  const isHovered     = hoveredCaseId === c.id;
                  const isHighlighted = highlightedCaseId === c.id;
                  const stats         = sessionStats[c.id] ?? { total: 0, completed: 0 };
                  const loading       = statsLoading[c.id] ?? false;

                  return (
                    <tr
                      key={c.id}
                      onMouseEnter={() => setHoveredCaseId(c.id)}
                      onMouseLeave={() => setHoveredCaseId(null)}
                      onClick={() =>
                        navigate(c.isSample ? routes.sampleCase : routes.viewCase(c.id))
                      }
                      style={{
                        cursor         : "pointer",
                        backgroundColor: isHighlighted ? "#FFF3CD" : "#ffffff",
                        borderBottom   : "1px solid #dee2e6",
                      }}
                    >
                      <td className="p-2">{c.id}</td>
                      <td className="p-2">{c.case_name}</td>

                      <td className="p-2">
                        <SessionsCell stats={stats} loading={loading} />
                      </td>

                      {/* Shows admin's account creation date for sample case,
                          real created_at for normal cases, "—" if missing */}
                      <td className="p-2">
                        {c.created_at
                          ? new Date(c.created_at).toLocaleDateString()
                          : "—"}
                      </td>

                      {/* ACTIONS */}
                      <td className="p-2 position-relative" style={{ minWidth: "150px" }}>
                        {isHovered && (
                          <div
                            style={{
                              position : "absolute",
                              right    : "0.5rem",
                              top      : "50%",
                              transform: "translateY(-50%)",
                              display  : "flex",
                              gap      : "0.5rem",
                            }}
                          >
                            {/* VIEW — always visible */}
                            <Link
                              to={c.isSample ? routes.sampleCase : routes.viewCase(c.id)}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                color         : theme.colors.primary,
                                textDecoration: "underline",
                              }}
                            >
                              View
                            </Link>

                            {/* EDIT & DELETE — admin / enterprise only */}
                            {!_isClientUser && (
                              <>
                                <Link
                                  to={c.isSample ? "#" : routes.editCase(c.id)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (c.isSample) {
                                      e.preventDefault();
                                      toast.info(`Sample ${ClientConfig.cases.label} cannot be edited`);
                                    }
                                  }}
                                  style={{
                                    color         : c.isSample ? "#ccc" : theme.colors.success,
                                    textDecoration: "underline",
                                    cursor        : c.isSample ? "not-allowed" : "pointer",
                                  }}
                                >
                                  Edit
                                </Link>

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(c.id);
                                  }}
                                  style={{
                                    background    : "none",
                                    border        : "none",
                                    color         : c.isSample ? "#ccc" : theme.colors.danger,
                                    textDecoration: "underline",
                                    cursor        : c.isSample ? "not-allowed" : "pointer",
                                    padding       : 0,
                                  }}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="text-center p-3">
                    {searchTerm
                      ? `No ${ClientConfig.cases.label} found with "${searchTerm}"`
                      : ClientConfig.cases.emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── PAGINATION ──────────────────────────────────────────────────── */}
        {filteredCases.length > perPage && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="d-flex gap-2 align-items-center">
              <span>Show:</span>
              <select
                className="form-select form-select-sm"
                value={perPage}
                onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}
                style={{ width: "70px" }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>

            <div className="d-flex gap-2 align-items-center">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              <span>Page {currentPage} of {totalPages}</span>
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>

            <div className="d-flex gap-2 align-items-center">
              <span>Go to:</span>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={gotoPage}
                onChange={(e) => setGotoPage(e.target.value)}
                className="form-control form-control-sm"
                style={{ width: "60px" }}
              />
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  const p = Number(gotoPage);
                  if (p >= 1 && p <= totalPages) setCurrentPage(p);
                  setGotoPage("");
                }}
              >
                Go
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CaseManagement;